import { DeleteOutlined } from "@ant-design/icons";
import { Alert, Button, Image, Input, Space, Typography, theme } from "antd";
import { type CSSProperties, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface FeedbackImage {
	name: string;
	dataUrl: string;
}
export interface FeedbackDescriptionValue {
	text: string;
	images: FeedbackImage[];
}
const MAX_BYTES = 1024 * 1024;
const imageBytes = (image: FeedbackImage) => {
	const encoded = image.dataUrl.split(",")[1] ?? "";
	return (
		(encoded.length * 3) / 4 -
		(encoded.endsWith("==") ? 2 : encoded.endsWith("=") ? 1 : 0)
	);
};

export function FeedbackDescription({
	value = { text: "", images: [] },
	onChange,
	disabled,
	onBusyChange,
}: {
	value?: FeedbackDescriptionValue;
	onChange?: (value: FeedbackDescriptionValue) => void;
	disabled?: boolean;
	onBusyChange: (busy: boolean) => void;
}) {
	const { t } = useTranslation("feedback");
	const { token } = theme.useToken();
	const latest = useRef(value);
	latest.current = value;
	const busyRef = useRef(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const change = (next: FeedbackDescriptionValue) => {
		latest.current = next;
		onChange?.(next);
	};
	const add = async (files: File[]) => {
		if (disabled || busyRef.current || !files.length) return;
		busyRef.current = true;
		setBusy(true);
		onBusyChange(true);
		setError(null);
		try {
			if (latest.current.images.length + files.length > 3)
				throw new Error(t("tooMany"));
			const total = latest.current.images.reduce(
				(sum, image) => sum + imageBytes(image),
				0,
			);
			if (total + files.reduce((sum, file) => sum + file.size, 0) > MAX_BYTES)
				throw new Error(t("tooLarge"));
			if (
				files.some(
					(file) =>
						!["image/png", "image/jpeg", "image/webp"].includes(file.type),
				)
			)
				throw new Error(t("imageType"));
			const images = await Promise.all(
				files.map(
					(file) =>
						new Promise<FeedbackImage>((resolve, reject) => {
							const reader = new FileReader();
							reader.onload = () =>
								resolve({
									name: file.name.slice(0, 120) || "image.png",
									dataUrl: String(reader.result),
								});
							reader.onerror = () => reject(new Error(t("readFailed")));
							reader.readAsDataURL(file);
						}),
				),
			);
			change({
				...latest.current,
				images: [...latest.current.images, ...images],
			});
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : t("readFailed"));
		} finally {
			busyRef.current = false;
			setBusy(false);
			onBusyChange(false);
		}
	};
	return (
		<div>
			<div
				className="crest-feedback-description"
				aria-busy={busy}
				style={
					{
						"--feedback-border": token.colorBorder,
						"--feedback-focus": token.colorPrimary,
						"--feedback-outline": token.controlOutline,
						borderRadius: token.borderRadius,
						background: disabled
							? token.colorBgContainerDisabled
							: token.colorBgContainer,
					} as CSSProperties
				}
			>
				<Input.TextArea
					value={value.text}
					disabled={disabled}
					rows={5}
					maxLength={4000}
					variant="borderless"
					aria-label={t("description")}
					placeholder={t("descriptionPlaceholder")}
					onChange={(event) =>
						change({ ...latest.current, text: event.target.value })
					}
					onPaste={(event) => {
						const files = Array.from(event.clipboardData.items)
							.filter((item) => item.kind === "file")
							.map((item) => item.getAsFile())
							.filter((file): file is File => Boolean(file));
						if (files.length) {
							if (!event.clipboardData.getData("text/plain"))
								event.preventDefault();
							void add(files);
						}
					}}
				/>
				<Image.PreviewGroup>
					<Space wrap>
						{value.images.map((image, index) => (
							<Space
								key={`${index}-${image.name}`}
								direction="vertical"
								size={4}
							>
								<Image
									src={image.dataUrl}
									alt={image.name}
									width={96}
									height={72}
									style={{ objectFit: "contain" }}
								/>
								<Button
									size="small"
									icon={<DeleteOutlined />}
									disabled={disabled || busy}
									aria-label={t("removeImage", { index: index + 1 })}
									onClick={() =>
										change({
											...latest.current,
											images: latest.current.images.filter(
												(_, i) => i !== index,
											),
										})
									}
								>
									{t("remove")}
								</Button>
							</Space>
						))}
					</Space>
				</Image.PreviewGroup>
				<div className="crest-feedback-description-count">
					<Typography.Text type="secondary">
						{value.text.length} / 4000
					</Typography.Text>
				</div>
			</div>
			<Typography.Text type="secondary">{t("imageHint")}</Typography.Text>
			{error && (
				<Alert type="error" showIcon message={error} style={{ marginTop: 8 }} />
			)}
		</div>
	);
}
